import React, { useState, useRef, useCallback } from 'react';
import classes from './KiwiHotelCardImage.scss';

/* Heart SVG — outlined or filled */
const HeartIcon: React.FC<{ filled: boolean }> = ({ filled }) =>
  filled ? (
    <svg viewBox="0 0 24 24" fill="#ef4444" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
    </svg>
  );

interface IKiwiHotelCardImage {
  images: string[];
  alt: string;
  isFavorite?: boolean;
  onFavoriteToggle?: () => void;
}

const MAX_DOTS = 5;

const KiwiHotelCardImage: React.FC<IKiwiHotelCardImage> = ({ images, alt, isFavorite, onFavoriteToggle }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);

  const goTo = useCallback(
    (index: number) => {
      if (index < 0 || index >= images.length) return;
      setCurrentIndex(index);
    },
    [images.length],
  );

  /* ── Touch handlers ── */
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 40) {
      if (diff > 0 && currentIndex < images.length - 1) {
        setCurrentIndex((prev) => prev + 1);
      } else if (diff < 0 && currentIndex > 0) {
        setCurrentIndex((prev) => prev - 1);
      }
    }
  };

  /* ── Mouse drag handlers ── */
  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    isDragging.current = true;
    dragStartX.current = e.clientX;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current) return;
    e.preventDefault();
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const diff = dragStartX.current - e.clientX;
    if (Math.abs(diff) > 40) {
      if (diff > 0 && currentIndex < images.length - 1) {
        setCurrentIndex((prev) => prev + 1);
      } else if (diff < 0 && currentIndex > 0) {
        setCurrentIndex((prev) => prev - 1);
      }
    }
  };

  const handleMouseLeave = () => {
    isDragging.current = false;
  };

  /* ── Visible dots (sliding window, max 5) ── */
  const getVisibleDots = () => {
    if (images.length <= MAX_DOTS) {
      return images.map((_, i) => i);
    }
    let start = currentIndex - Math.floor(MAX_DOTS / 2);
    if (start < 0) start = 0;
    if (start + MAX_DOTS > images.length) start = images.length - MAX_DOTS;
    return Array.from({ length: MAX_DOTS }, (_, i) => start + i);
  };

  if (images.length === 0) {
    return (
      <div className={classes.wrapper}>
        <div className={classes.placeholder}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
            <path d="M7 13c1.66 0 3-1.34 3-3S8.66 7 7 7s-3 1.34-3 3 1.34 3 3 3zm12-6h-8v7H3V5H1v15h2v-3h18v3h2v-9c0-2.21-1.79-4-4-4z" />
          </svg>
        </div>
        {onFavoriteToggle && (
          <button
            type="button"
            className={classes.favoriteBtn}
            onClick={(e) => {
              e.stopPropagation();
              onFavoriteToggle();
            }}
          >
            <HeartIcon filled={!!isFavorite} />
          </button>
        )}
      </div>
    );
  }

  const visibleDots = getVisibleDots();

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className={classes.wrapper} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseLeave}>
      {/* Slides */}
      <div className={classes.track} style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
        {images.map((url, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div className={classes.slide} key={i}>
            <img src={url} alt={`${alt} ${i + 1}`} className={classes.slideImage} loading="lazy" draggable={false} />
          </div>
        ))}
      </div>

      {/* Favorite */}
      {onFavoriteToggle && (
        <button
          type="button"
          className={classes.favoriteBtn}
          onClick={(e) => {
            e.stopPropagation();
            onFavoriteToggle();
          }}
        >
          <HeartIcon filled={!!isFavorite} />
        </button>
      )}

      {/* Dots */}
      {images.length > 1 && (
        <div className={classes.dots}>
          {visibleDots.map((i) => (
            <button
              key={i}
              type="button"
              className={`${classes.dot} ${i === currentIndex ? classes.dotActive : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                goTo(i);
              }}
              aria-label={`Image ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default KiwiHotelCardImage;
